<template>
  <li class="hospitalCassette">
    <div
      class="hospitalCassette_bar"
      :class="{ '-active': liveStatus == 'live' }"
    >
      <span v-if="liveStatus === 'live'">配信中</span>
      {{ hospitalStart }}〜{{ hospitalEnd }}
    </div>

    <div class="hospitalCassette_inner">
      <div class="hospitalCassette_flex">
        <p
          class="hospitalCassette_ttl hospitalCassette_flex_l"
          :class="{ '-checked': canRequestCheck }"
        >
          {{ casseteTitle }}
        </p>
        <div
          class="hospitalCassette_img"
          :class="{
            '-disabled': liveStatus === 'after'
          }"
          @click="() => viewLive(channelName, programStartTime, '参加病院')"
        >
          <img
            v-if="detail.basicinfo_image1"
            :src="detail.basicinfo_image1"
            :alt="detail.hospital_name"
          />
          <img
            v-else
            src="@/assets/images/common/noimg.svg"
            :alt="detail.hospital_name"
          />
          <div
            class="hospitalCassette_img_overlay"
            :class="{ '-disabled': liveStatus == 'after' }"
          >
            <img
              v-if="liveStatus == 'after'"
              src="@/assets/images/events/play.png"
              :alt="liveStatus"
            />
            <img
              v-else
              src="@/assets/images/events/play_active.png"
              :alt="liveStatus"
            />
          </div>
          <div
            class="hospitalCassette_img_text"
            :class="{
              '-active': liveStatus !== 'after',
              '-disabled': liveStatus == 'after'
            }"
          >
            {{ thumbLabel }}
          </div>
        </div>
      </div>
      <span class="hospitalCassette_tag" v-for="item in label" :key="item">
        {{ item }}
      </span>
    </div>

    <div class="hospitalCassette_inner hospitalCassette_middle">
      <div class="hospitalCassette_flex">
        <div class="hospitalCassette_flex_l">
          <HospitalLabel :items="[detail.prefectures]" :isOnline="true" />

          <div class="hospitalCassette_class">
            {{ detail.hospital_class }}
          </div>

          <p class="hospitalCassette_hospital">
            {{ detail.hospital_name }}
          </p>
        </div>

        <div
          class="hospitalCassette_fav"
          :class="{
            '-active': favBtnLabel == '登録済み'
          }"
        >
          <span>{{ favBtnLabel }}</span>
        </div>
      </div>

      <HospitalTag
        v-if="features.length > 0"
        class="hospitalCassette_feature"
        :tags="features"
      />
    </div>

    <div class="hospitalCassette_btns" v-if="!enableRequest">
      <Button
        :label="btnLabel"
        :handleClick="() => viewLive(channelName, programStartTime, '参加病院')"
        :disabled="liveStatus === 'after'"
        secondary
      />
      <Button
        :label="'病院詳細を見る'"
        :handleClick="
          () => $router.push(`/search/hospital/detail/${detail.hospital_id}`)
        "
        primary
      />
    </div>

    <div class="hospitalCassette_checkbox" v-if="canRequestCheck">
      <input
        type="checkbox"
        :name="`${detail.hospital_id}_check`"
        :id="`${detail.hospital_id}_check`"
        :value="detail.hospital_id"
        @change="changeInput($event)"
      /><label
        :for="`${detail.hospital_id}_check`"
        class="hospitalCassette_checkboxLabel"
      ></label>
    </div>
  </li>
</template>

<script lang="ts">
import { defineComponent, SetupContext, computed, PropType } from 'vue'
import { useStore } from 'vuex'
import Button from '@/components/elements/Button.vue'
import HospitalLabel from '@/components/elements/HospitalLabel.vue'
import HospitalTag from '@/components/elements/HospitalTag.vue'

export default defineComponent({
  name: 'HospitalCassetteOnlineEvent',

  props: {
    detail: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: ''
    },
    label: {
      type: Array as PropType<string[]>,
      default: []
    },
    channelName: {
      type: String,
      required: true
    },
    channelDescription: {
      type: String,
      required: true
    },
    liveStatus: {
      type: String as PropType<'live' | 'soon' | 'before' | 'after'>,
      required: true
    },
    programStartTime: {
      type: String,
      required: true
    },
    hospitalStart: {
      type: String,
      required: true
    },
    hospitalEnd: {
      type: String,
      required: true
    },
    enableRequest: {
      type: Boolean,
      default: false
    },
    viewLive: {
      type: Function as PropType<
        (
          channelName: string,
          selectedProgramStart: string,
          btnType?: string
        ) => void
      >,
      required: true
    }
  },

  components: {
    Button,
    HospitalLabel,
    HospitalTag
  },

  setup(props, context: SetupContext) {
    const store = useStore()

    const features = props.detail.discerning_conditions
      ? props.detail.discerning_conditions.map((el: string) => {
          return {
            label: el,
            disabled: false
          }
        })
      : []

    const btnLabel = computed((): string => {
      if (props.liveStatus === 'before')
        return props.programStartTime + '配信予定'
      if (['soon', 'live'].includes(props.liveStatus)) return '配信内容を見る'
      if (props.liveStatus === 'after') return '配信終了'
      return '配信終了'
    })

    const thumbLabel = computed((): string => {
      if (props.liveStatus === 'before')
        return props.programStartTime + '配信予定'
      if (['soon', 'live'].includes(props.liveStatus)) return '配信内容を見る'
      if (props.liveStatus === 'after') return '配信終了'
      return '配信終了'
    })

    const isFavorite = computed((): boolean => {
      if (store.state.isLogin) {
        const favoriteIds = store.state.userInfo.favorite_history.map(
          (el: Record<'id' | 'hospital_id', number>) => {
            return el.hospital_id
          }
        )
        return favoriteIds.includes(props.detail.hospital_id)
      }
      return false
    })

    const favBtnLabel = computed((): string => {
      return isFavorite.value ? '登録済み' : '未登録'
    })

    const canRequestCheck = computed((): boolean => {
      return (
        props.enableRequest &&
        props.detail.paid_flg === 1 &&
        props.detail.active_flg === 3
      )
    })

    const changeInput = (e: any) => {
      context.emit('call-input-change', {
        value: e.target.value,
        checked: e.target.checked
      })
    }

    const casseteTitle = computed((): string => {
      const number = 30
      const ttl = props.channelDescription
        ? props.channelDescription
        : props.title
      return ttl.length > number ? ttl.substr(0, number) + '…' : ttl
    })

    return {
      features,
      btnLabel,
      thumbLabel,
      isFavorite,
      favBtnLabel,
      canRequestCheck,
      changeInput,
      casseteTitle
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.hospitalCassette {
  position: relative;
  border-radius: 5px;
  background: #fff;
  border: 1px solid rgb(228, 228, 228);
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.08);
  font-size: 1.3rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &_bar {
    padding: 5px;
    color: #fff;
    background: var.$color-main;
    text-align: center;
    font-size: 1.3rem;
    font-weight: 700;

    &.-active {
      background: var.$color-primary;
    }
    span {
      font-size: 1rem;
      color: var.$color-primary;
      background-color: #fff;
      height: 18px;
      padding: 0 4px;
      display: inline-flex;
      align-items: center;
      margin-right: 7px;
    }
  }

  // &_fav {
  //   display: flex;
  //   justify-content: center;
  //   align-items: center;
  //   width: 100%;
  //   // text-align: center;
  //   background-color: #f5f5f5;
  //   padding: 10px;
  //   font-size: 1.3rem;
  //   @include mixins.media() {
  //     display: none;
  //   }

  //   &::before {
  //     content: '';
  //     display: block;
  //     margin-right: 8px;
  //     width: 20px;
  //     height: 18px;
  //     background-image: url('~@/assets/icon/heart.svg');
  //     background-size: 20px 18px;
  //   }

  //   &.-active::before {
  //     background-image: url('~@/assets/icon/heart_on.svg');
  //     background-size: 20px 18px;
  //   }
  // }

  &_inner {
    padding: 20px;
    // border-bottom: 1px solid var.$color-border-gray;
  }

  &_flex {
    width: 100%;
    display: flex;
    &_l {
      flex: 1;
    }
  }

  &_ttl {
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 1.6;
    &.-checked {
      padding-left: 30px;
    }
  }

  &_img {
    flex-basis: 150px;
    margin-left: 24px;
    position: relative;
    cursor: pointer;

    &.-disabled {
      cursor: default;
      pointer-events: none;
      cursor: not-allowed;
    }

    &_overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: calc(100% - 24px);
      &.-disabled {
        background-color: rgba(0, 0, 0, 0.5);
        img {
          display: none;
        }
      }

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    &_text {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 24px;
      font-size: 12px;
      font-weight: 700;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #fff;
      background-color: var.$color-main;
      &.-active {
        background-color: var.$color-primary;
      }
      &.-disabled {
        background-color: #d3d3d3;
      }
    }
  }

  &_tag {
    margin-top: 10px;
    display: inline-block;
    background-color: var.$color-secondary;
    padding: 4px;
    line-height: 1;
    font-size: 11px;
    & + & {
      margin-left: 8px;
    }
  }

  &_class {
    display: inline-block;
    font-size: 1rem;
    padding: 6px 10px;
    color: #666;
    font-weight: 700;
    line-height: 1;
    border: 1px solid #666;
    border-radius: 2px;
  }

  &_hospital {
    margin-top: 10px;
    font-size: 1.6rem;
    line-height: 1.5;
  }

  &_fav {
    flex-basis: 70px;
    height: 50px;
    background-color: var.$color-other-bg;
    border: var.$color-border-gray;
    border-radius: 4px;
    margin-left: 14px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    &::before {
      content: '';
      display: block;
      width: 20px;
      height: 18px;
      background-image: url('~@/assets/icon/heart.svg');
      background-size: 20px 18px;
    }
    span {
      color: #666;
      font-size: 1rem;
      line-height: 1;
      margin-top: 3px;
    }

    &.-active::before {
      background-image: url('~@/assets/icon/heart_on.svg');
      background-size: 20px 18px;
    }
  }

  &_feature {
    margin-top: 21px;
  }

  &_middle {
    border-top: 1px solid var.$color-border-gray;
  }

  &_btns {
    margin-top: auto;
    border-top: 1px solid var.$color-border-gray;
    padding: 16px 12.5px;
    display: flex;
    align-items: center;
    > button {
      margin: 0 7.5px;
    }
  }

  &_checkbox {
    position: absolute;
    top: 57px;
    left: 18px;
    width: 20px;
    height: 20px;
    // transform: translate(30px, 80px);

    & > input[type='checkbox'] {
      display: none;
    }
  }

  &_checkboxLabel {
    position: relative;
    transition: background 0.1s ease, border 0.1s ease;

    &::before {
      display: inline-block;
      content: '';
      width: 20px;
      height: 20px;
      background-color: var.$color-border-gray;
      border-radius: 4px;
      flex-shrink: 0;
      transition: background 0.1s ease;
    }

    &::after {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
      display: inline-block;
      content: '';
      width: 12px;
      height: 8px;
      border-left: 2px solid #fff;
      border-bottom: 2px solid #fff;
      transform: rotate(-45deg) translate(4px, 2px);
      transition: opacity 0.1s ease;
    }

    .hospitalCassette_checkbox > input[type='checkbox']:checked + & {
      &::before {
        background-color: var.$color-main;
      }
      &::after {
        opacity: 1;
      }
    }
  }
}
</style>
